import React, { createContext, useCallback, useContext, useEffect, useState } from "react"
import { useFlags } from "launchdarkly-react-client-sdk"
import { AirwallexSca } from "./AirwallexSca"
import useCreateAirwallexPanToken from "./hooks/useCreateAirwallexPanToken"
import { AirwallexCardDetails } from "./AirwallexCardDetails"
import { AirwallexPinDisplaySection } from "./AirwallexPinDisplaySection"
import { ShowErrorRetrievingCardDetails } from "../CarrotCardActiveModule"
import { Box, Progress } from "@carrotfertility/carotene-core"
import { reportError } from "#/utils/ErrorReporting"

interface AirwallexCardProps {
  hideCardDetails: () => void
  pinDisplaySupported?: boolean
}
const AirwallexCardAccess: React.FC<AirwallexCardProps> = ({ hideCardDetails, pinDisplaySupported }) => {
  const { scaToken } = useVerificationToken()
  const [error, setError] = useState(false)
  const [loadedToken, setLoadedToken] = useState<string | null>(null)
  const { airwallexCardPinDisplay } = useFlags()

  const {
    mutate: createPanToken,
    data: pantokenResponse,
    isLoading: pantokenIsLoading,
    isError: isErrorGettingPantoken,
    error: pantokenError
  } = useCreateAirwallexPanToken()

  const handleError = useCallback((error: any) => {
    reportError(error)
    setError(true)
  }, [])

  const currentToken = pantokenResponse?.token ?? null
  const handleDetailsLoaded = useCallback(() => setLoadedToken(currentToken), [currentToken])

  useEffect(() => {
    if (isErrorGettingPantoken) {
      handleError(`Error getting pan token: ${JSON.stringify(pantokenError, null, 2)}`)
    }
  }, [handleError, isErrorGettingPantoken, pantokenError])

  useEffect(() => {
    createPanToken(scaToken)
  }, [createPanToken, scaToken])

  // Bind readiness to the token that actually loaded: if the PAN token is later replaced,
  // the new card-details iframe hasn't reported loaded yet, so the PIN section must wait again.
  const detailsLoaded = currentToken !== null && loadedToken === currentToken
  const showCardPin = Boolean(airwallexCardPinDisplay) && Boolean(pinDisplaySupported)

  if (error) {
    return <ShowErrorRetrievingCardDetails />
  }

  if (pantokenIsLoading || !pantokenResponse || (!pantokenResponse?.token && scaToken)) {
    return (
      <Box width={"100%"} display="flex" alignItems="center" justifyContent="center" height="100px">
        <Progress />
      </Box>
    )
  }

  if (pantokenResponse?.token) {
    return (
      <>
        <AirwallexCardDetails
          onError={handleError}
          isLoading={pantokenIsLoading}
          panToken={pantokenResponse}
          onLoaded={handleDetailsLoaded}
        />
        {showCardPin && detailsLoaded && <AirwallexPinDisplaySection scaToken={scaToken} />}
      </>
    )
  }

  //No error, pan token is loaded and empty, so we need to do SCA
  return (
    <AirwallexSca onClose={hideCardDetails} onError={handleError} scaSessionCode={pantokenResponse?.sca_session_code} />
  )
}

const VerificationTokenContext = createContext(null)

const VerificationTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const [scaToken, setScaToken] = useState(null)

  return (
    <VerificationTokenContext.Provider value={{ scaToken, setScaToken }}>{children}</VerificationTokenContext.Provider>
  )
}

// Custom hook to use the verification token context
export const useVerificationToken = () => {
  return useContext(VerificationTokenContext)
}

export const AirwallexCard: React.FC<AirwallexCardProps> = ({ hideCardDetails, pinDisplaySupported }) => {
  return (
    <VerificationTokenProvider>
      <AirwallexCardAccess hideCardDetails={hideCardDetails} pinDisplaySupported={pinDisplaySupported} />
    </VerificationTokenProvider>
  )
}
